import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { X, Eye, EyeOff } from "lucide-react";
import Turnstile from "react-turnstile";

export default function PasswordResetModal({
  open,
  onClose,
  initialEmail = "",
}) {
  const [step, setStep] = useState(1); // 1: email, 2: otp, 3: new password
  const [email, setEmail] = useState(initialEmail);
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [oldPasswordHash, setOldPasswordHash] = useState("");
  const [checkingOld, setCheckingOld] = useState(false);
  const [resetTurnstileToken, setResetTurnstileToken] = useState("");

  const API = import.meta.env.VITE_BACKEND_URL;

  // Password requirements
  const passwordRequirements = [
    {
      label: "At least 8 characters",
      test: (pw) => pw.length >= 8,
    },
    {
      label: "A lowercase letter (a-z)",
      test: (pw) => /[a-z]/.test(pw),
    },
    {
      label: "An uppercase letter (A-Z)",
      test: (pw) => /[A-Z]/.test(pw),
    },
    {
      label: "A number (0-9)",
      test: (pw) => /[0-9]/.test(pw),
    },
    {
      label: "A symbol (e.g. !@#$%^&*)",
      test: (pw) => /[^A-Za-z0-9]/.test(pw),
    },
  ];

  useEffect(() => {
    setEmail(initialEmail);
  }, [initialEmail, open]);

  useEffect(() => {
    if (!open) {
      setStep(1);
      setEmail(initialEmail);
      setOtp("");
      setNewPassword("");
      setConfirmPassword("");
      setLoading(false);
      setError("");
      setMessage("");
    }
  }, [open, initialEmail]);

  // After OTP is verified, fetch old password hash for comparison
  useEffect(() => {
    if (step === 3 && email) {
      setCheckingOld(true);
      axios
        .post(`${API}/api/auth/profile/check-old-password`, { email })
        .then((res) => {
          setOldPasswordHash(res.data.passwordHash || "");
        })
        .catch(() => setOldPasswordHash(""))
        .finally(() => setCheckingOld(false));
    }
  }, [step, email]);

  const validatePassword = (password) => {
    if (password.length < 8) return "Password must be at least 8 characters.";
    if (!/[a-z]/.test(password))
      return "Password must include a lowercase letter.";
    if (!/[A-Z]/.test(password))
      return "Password must include an uppercase letter.";
    if (!/[0-9]/.test(password)) return "Password must include a number.";
    if (!/[^A-Za-z0-9]/.test(password))
      return "Password must include a symbol.";
    return null;
  };

  const handleRequestReset = async () => {
    setError("");
    setMessage("");
    if (!resetTurnstileToken) {
      setError("Please complete the CAPTCHA challenge.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/profile/request-password-reset`, {
        email: email.trim().toLowerCase(),
        turnstileToken: resetTurnstileToken,
      });
      setStep(2);
      setMessage("OTP sent to your email.");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError("");
    setMessage("");
    if (!otp) {
      setError("Please enter the OTP sent to your email.");
      return;
    }
    setStep(3);
  };

  const handleResetPassword = async () => {
    setError("");
    setMessage("");
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    // Check if new password is same as old
    if (oldPasswordHash && newPassword) {
      try {
        const res = await axios.post(
          `${API}/api/auth/profile/compare-password`,
          {
            email: email.trim().toLowerCase(),
            newPassword,
          }
        );
        if (res.data.isSame) {
          setError("New password must not be the same as your old password.");
          return;
        }
      } catch {
        // If error, allow reset (fail open)
      }
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/profile/verify-reset-otp`, {
        email: email.trim().toLowerCase(),
        otp,
        newPassword,
      });
      setMessage("Password updated! Please log in with your new password.");
      setTimeout(() => {
        localStorage.removeItem("token");
        window.location.href = "/";
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
      <div className="bg-card rounded-lg p-6 w-full max-w-md max-h-[90vh] flex flex-col mx-4 sm:px-6">
        <div className="sticky top-0 bg-card">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Reset Password</h2>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Close"
            >
              <X size={24} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {step === 1 && (
            <div className="flex flex-col items-stretch justify-center h-full mx-1">
              <p className="text-sm text-foreground mb-4 text-left">
                Enter your email address to receive a password reset OTP.
              </p>
              {(error || message) && (
                <div className="w-full mb-2">
                  {error && <div className="text-red-600 text-sm">{error}</div>}
                  {message && (
                    <div className="text-green-600 text-sm">{message}</div>
                  )}
                </div>
              )}
              <input
                type="email"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-ring mb-4"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                disabled={loading}
              />
              <div style={{ width: 200, overflow: "hidden" }}>
                <Turnstile
                  sitekey="0x4AAAAAABk3IIa_Na1Xi2qg"
                  onSuccess={setResetTurnstileToken}
                  onExpire={() => setResetTurnstileToken("")}
                />
              </div>
              <button
                type="button"
                className="w-full px-4 py-2 mt-3 bg-foreground text-background rounded-md shadow-md hover:bg-foreground/90 transition-all text-sm font-medium disabled:opacity-60"
                onClick={handleRequestReset}
                disabled={loading || !email || !resetTurnstileToken}
              >
                {loading ? "Sending OTP..." : "Send OTP"}
              </button>
            </div>
          )}
          {step === 2 && (
            <div className="flex flex-col items-stretch justify-center h-full mx-1">
              <p className="text-sm text-foreground mb-4 text-left">
                Enter the OTP sent to your email address.
              </p>
              {(error || message) && (
                <div className="w-full mb-2">
                  {error && <div className="text-red-600 text-sm">{error}</div>}
                  {message && (
                    <div className="text-green-600 text-sm">{message}</div>
                  )}
                </div>
              )}
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-ring mb-4"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                autoFocus
                disabled={loading}
              />
              <button
                type="button"
                className="w-full px-4 py-2 bg-foreground text-background rounded-md shadow-md hover:bg-foreground/90 transition-all text-sm font-medium disabled:opacity-60"
                onClick={handleVerifyOtp}
                disabled={loading || !otp}
              >
                {loading ? "Verifying OTP..." : "Verify OTP"}
              </button>
            </div>
          )}
          {step === 3 && (
            <div className="flex flex-col items-stretch justify-center h-full mx-1">
              <p className="text-sm text-foreground mb-4 text-left">
                Enter your new password.
              </p>
              {(error || message) && (
                <div className="w-full mb-2">
                  {error && <div className="text-red-600 text-sm">{error}</div>}
                  {message && (
                    <div className="text-green-600 text-sm">{message}</div>
                  )}
                </div>
              )}
              <div className="relative w-full mb-2">
                <input
                  type={showNewPassword ? "text" : "password"}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-ring pr-10"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoFocus
                  disabled={loading}
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                  onClick={() => setShowNewPassword((v) => !v)}
                  aria-label={
                    showNewPassword ? "Hide password" : "Show password"
                  }
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {/* Password requirements checklist */}
              <ul className="text-xs text-muted-foreground mb-2 w-full">
                {passwordRequirements.map((req, i) => (
                  <li key={i} className="flex items-center gap-1">
                    <span
                      className={
                        req.test(newPassword)
                          ? "text-green-600 font-bold"
                          : "text-muted-foreground"
                      }
                    >
                      •
                    </span>
                    <span
                      className={
                        req.test(newPassword)
                          ? "text-green-600 font-bold"
                          : "text-muted-foreground"
                      }
                    >
                      {req.label}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="relative w-full mb-4">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-ring pr-10"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  aria-label={
                    showConfirmPassword ? "Hide password" : "Show password"
                  }
                >
                  {showConfirmPassword ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              </div>
              <button
                type="button"
                className="w-full px-4 py-2 bg-foreground text-background rounded-md shadow-md hover:bg-foreground/90 transition-all text-sm font-medium disabled:opacity-60"
                onClick={handleResetPassword}
                disabled={loading || !newPassword || !confirmPassword}
              >
                {loading ? "Resetting Password..." : "Reset Password"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
